import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  AssessmentSubmissionForGrading,
  AssessmentType,
  IleCompletionStatus,
  Module,
  UnifiedSubmission,
  useBulkApproveAssessmentGradesMutation,
  useGetAssessmentQuery,
  useGetLmsLinkedUserIlEsQuery,
  useGetAssessmentSubmissionsForGradingQuery,
  useUpdateAssessmentConfigMutation,
} from "@axio/graphql/generated/graphql";
import { useAuth } from "../hooks/useAuth";
import Sentry from "../utils/sentry";
import type { RootStackParamList } from "../navigation/navigation.types";
import LTILayout from "../layouts/LTILayout";
import Loader from "../components/common/status/Loader/Loader";
import LTIErrorView from "./LTIErrorView";
import { hasMinRole, UserRoleUI } from "../constants/rolesAndPermissions";
import AdminTabNavigator from "../components/adminDashboard/AdminTabNavigator/AdminTabNavigator";
import GradingCenterTable from "../components/adminDashboard/GradingCenter/GradingCenterTable";
import GradingPanel from "../components/adminDashboard/GradingCenter/GradingPanel/GradingPanel";
import { GradingCenterModalState } from "../components/adminDashboard/GradingCenter/types";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomModal from "../components/common/general/CustomModal/CustomModal";
import AssessmentForm from "../components/customCoursework/ActivityForms/AssessmentForm/AssessmentForm";
import { useToast } from "../contexts/Toast/ToastProvider";
import { GradingCenterSubmissionsProvider, useGradingCenterSubmissions } from "../contexts/GradingCenterContext";
import { getInitials } from "../utils/getInitials";

const SUBMISSIONS_TAB_KEY = "submissions-grading";
const EDIT_ASSESSMENT_TAB_KEY = "edit-assessment";

type AssessmentFormActivityData = React.ComponentProps<typeof AssessmentForm>["activityData"];

function LTIAssessmentGradingViewContent() {
  const route = useRoute<RouteProp<RootStackParamList, "LTI Assessment Grading">>();
  const searchParams = useMemo(() => {
    if (typeof window === "undefined") {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { assessmentId, accessToken: routeAccessToken, refreshToken: routeRefreshToken } = route.params ?? {};
  const integrationId =
    route.params?.integrationId ?? searchParams.get("integrationId") ?? searchParams.get("integration_id") ?? "";
  const { setFilteredSubmissions, setSelectedSubmissionData } = useGradingCenterSubmissions();
  const { login, isAuthenticated, didCheckToken, userInfo } = useAuth();
  // useToast() is null outside a ToastProvider; fall back to a no-op so callers
  // don't need to null-check at every call site.
  const showToast = useToast()?.showToast ?? (() => ({ id: "", close: () => {} }));
  const { t } = useTranslation();

  const canEdit = hasMinRole(userInfo.userRole, UserRoleUI.FACULTY);
  const [authReady, setAuthReady] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(SUBMISSIONS_TAB_KEY);
  const [gradingModalState, setGradingModalState] = useState<GradingCenterModalState>(GradingCenterModalState.CLOSED);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<string[]>([]);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isPublishConfirmModalVisible, setIsPublishConfirmModalVisible] = useState(false);
  const [assessmentSaveError, setAssessmentSaveError] = useState<string>("");

  const [bulkApproveAssessmentGrades, { loading: isBulkApproving }] = useBulkApproveAssessmentGradesMutation();
  const [updateAssessmentConfigMutation, { loading: isSavingAssessment }] = useUpdateAssessmentConfigMutation();

  useFocusEffect(
    useCallback(() => {
      if (!routeAccessToken || !routeRefreshToken) return;

      let isActive = true;

      const bootstrapAuth = async () => {
        try {
          await login(routeAccessToken, routeRefreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
          });
          return;
        }

        if (!isActive) return;
        setAuthReady(true);
      };

      void bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [login, navigation, routeAccessToken, routeRefreshToken])
  );

  useEffect(() => {
    if (routeAccessToken && routeRefreshToken) return;
    if (!didCheckToken) return;

    if (isAuthenticated) {
      setAuthReady(true);
    } else {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
      });
    }
  }, [didCheckToken, isAuthenticated, navigation, routeAccessToken, routeRefreshToken]);

  useEffect(() => {
    if (!authReady || assessmentId) return;

    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
    });
  }, [authReady, assessmentId, navigation]);

  const { data: assessmentData, loading: assessmentLoading } = useGetAssessmentQuery({
    variables: { assessmentId: assessmentId ?? "" },
    skip: !authReady || !assessmentId,
    fetchPolicy: "network-only",
  });

  const {
    data: gradingData,
    loading: gradingLoading,
    refetch: refetchGradingData,
  } = useGetAssessmentSubmissionsForGradingQuery({
    variables: { assessmentId: assessmentId ?? "" },
    skip: !authReady || !assessmentId,
    fetchPolicy: "network-only",
  });
  const submissions = gradingData?.getAssessmentSubmissionsForGrading?.data ?? [];

  const { data: userIlesData } = useGetLmsLinkedUserIlEsQuery({
    variables: {
      integrationId,
      completionStatus: IleCompletionStatus.Active,
    },
    skip: !authReady || !integrationId,
    fetchPolicy: "cache-and-network",
  });

  const lmsAiTutors = useMemo(() => {
    const linkedIles =
      userIlesData?.getLmsLinkedUserILEs?.data
        ?.flatMap((ile) => (ile ? [{ id: ile.id, title: ile.title }] : []))
        .filter(Boolean) ?? [];

    const linkedIds = new Set(linkedIles.map((ile) => ile.id));
    const existingAssessmentTutors =
      assessmentData?.getAssessment?.data?.aiTutors
        ?.flatMap((tutor) => (tutor ? [{ id: tutor.id, title: tutor.title }] : []))
        .filter((tutor) => !linkedIds.has(tutor.id)) ?? [];

    return [...linkedIles, ...existingAssessmentTutors];
  }, [assessmentData?.getAssessment?.data?.aiTutors, userIlesData]);

  useEffect(() => {
    setSelectedSubmissionIds((currentSelectedIds) =>
      currentSelectedIds.filter((id) => submissions.some((submission) => submission.id === id))
    );
  }, [submissions]);

  const handleToggleSubmissionSelection = (submissionId: string) => {
    setSelectedSubmissionIds((currentSelectedIds) => {
      if (currentSelectedIds.includes(submissionId)) {
        return currentSelectedIds.filter((id) => id !== submissionId);
      }

      return [...currentSelectedIds, submissionId];
    });
  };

  const handleToggleSelectAllSubmissions = () => {
    setSelectedSubmissionIds((currentSelectedIds) => {
      if (currentSelectedIds.length === submissions.length) {
        return [];
      }

      return submissions.map((submission) => submission.id);
    });
  };

  const handleBulkApproveConfirm = async () => {
    if (selectedSubmissionIds.length <= 1 || !assessmentId) {
      return;
    }

    try {
      const response = await bulkApproveAssessmentGrades({
        variables: {
          submissionIds: selectedSubmissionIds,
        },
      });

      const payload = response?.data?.bulkApproveAssessmentGrades;
      if (!payload?.success) {
        showToast({
          message: payload?.message || "Failed to approve selected grades.",
          type: "error",
        });
        return;
      }

      showToast({
        message: payload.message || "Grades approved and posted successfully.",
        type: "success",
      });

      setSelectedSubmissionIds([]);
      setIsConfirmModalVisible(false);
      await refetchGradingData({ assessmentId: assessmentId });
    } catch (error) {
      Sentry.captureException(error);
      showToast({
        message: "Failed to approve selected grades.",
        type: "error",
      });
    }
  };

  const handleSingleSubmissionPublish = useCallback(
    async (submission: UnifiedSubmission): Promise<{ success: boolean; message: string }> => {
      if (!assessmentId) {
        return {
          success: false,
          message: "Assessment context is incomplete.",
        };
      }

      try {
        const response = await bulkApproveAssessmentGrades({
          variables: {
            submissionIds: [submission.id],
          },
        });

        const payload = response?.data?.bulkApproveAssessmentGrades;

        if (!payload?.success) {
          return {
            success: false,
            message: payload?.message || "Failed to publish selected grade.",
          };
        }

        await refetchGradingData({ assessmentId });

        return {
          success: true,
          message: payload.message || `Grade published for ${submission.student?.name}`,
        };
      } catch (error) {
        Sentry.captureException(error);
        return {
          success: false,
          message: "Failed to publish selected grade.",
        };
      }
    },
    [assessmentId, bulkApproveAssessmentGrades, refetchGradingData]
  );

  const handlePublishAllConfirm = async () => {
    // "Publish All" sends every submission's grade to the LMS, regardless of the
    // current row selection (which only drives "Approve & Post" / approve-selected).
    const allSubmissionIds = submissions.map((submission) => submission.id);
    if (allSubmissionIds.length === 0 || !assessmentId) {
      return;
    }

    try {
      const response = await bulkApproveAssessmentGrades({
        variables: {
          submissionIds: allSubmissionIds,
        },
      });

      const payload = response?.data?.bulkApproveAssessmentGrades;
      if (!payload?.success) {
        showToast({
          message: payload?.message || "Failed to publish selected grades.",
          type: "error",
        });
        return;
      }

      showToast({
        message: payload.message || "Grades published to LMS successfully.",
        type: "success",
      });

      setSelectedSubmissionIds([]);
      setIsPublishConfirmModalVisible(false);
      await refetchGradingData({ assessmentId: assessmentId });
    } catch (error) {
      Sentry.captureException(error);
      showToast({
        message: "Failed to publish selected grades.",
        type: "error",
      });
    }
  };

  const assessment = assessmentData?.getAssessment?.data;
  // The Assessment node has no title of its own — its display name is the title
  // of the module it belongs to.
  const assessmentName = assessment?.moduleHasAssessment?.title;
  const moduleId = assessment?.moduleHasAssessment?.id;
  const courseId = assessment?.moduleHasAssessment?.courseHasModules?.[0]?.id;

  const unifiedLtiSubmissions = useMemo<UnifiedSubmission[]>(() => {
    return submissions.map((submission) => ({
      id: submission.id,
      submissionType: "Assessment",
      submissionTitle: assessmentName || "Assessment",
      courseName: "LTI Course",
      moduleName: assessmentName || "Assessment",
      submittedOn: submission.submittedAt,
      status: submission.status,
      grade: submission.totalPoints,
      maximumPoints: submission.maximumPoints ?? 0,
      dueDate: assessment?.dueDate,
      retakeDeadline: null,
      retakeDeadlineSnapshot: null,
      isRetake: false,
      student: {
        id: submission.userId,
        name: submission.userName || "Unknown Student",
        email: submission.userEmail || "",
        initials: getInitials(submission.userName),
      },
    })) as UnifiedSubmission[];
  }, [assessment?.dueDate, assessmentName, submissions]);

  const handleLtiEditGradePress = (submission: AssessmentSubmissionForGrading) => {
    const selectedSubmission = unifiedLtiSubmissions.find((item) => item.id === submission.id);
    if (!selectedSubmission) {
      showToast({
        message: "Unable to open grading panel for this submission.",
        type: "error",
      });
      return;
    }

    setSelectedSubmissionData(selectedSubmission);
    setGradingModalState(GradingCenterModalState.VIEW_ASSESSMENT_RESPONSE);
  };

  useEffect(() => {
    setFilteredSubmissions(unifiedLtiSubmissions);
  }, [setFilteredSubmissions, unifiedLtiSubmissions]);

  const handleGradingActionResult = (message: string) => {
    if (!message) {
      return;
    }

    showToast({
      message,
      type: "success",
    });
  };

  const handleGradingUpdateError = (message: string | null) => {
    if (!message) {
      return;
    }

    showToast({
      message,
      type: "error",
    });
  };

  const activeModuleData = useMemo(() => {
    if (!moduleId || !assessment?.id) {
      return null;
    }

    return {
      id: moduleId,
      assessment: {
        id: assessment.id,
      },
    } as Module;
  }, [assessment?.id, moduleId]);

  const assessmentActivityData = useMemo<AssessmentFormActivityData | null>(() => {
    if (!assessment) {
      return null;
    }

    return {
      id: assessment.id,
      assessmentId: assessment.id,
      assessmentType: assessment.assessmentType ?? AssessmentType.SocraticDialog,
      numberOfQuestions: assessment.numberOfQuestions ?? 10,
      timeLimit: assessment.timeLimit ?? 0,
      selectedAiTutorIds: assessment.aiTutors?.map((tutor) => tutor.id) ?? [],
      freeTextInstructions: assessment.freeTextInstructions ?? "",
      dueDate: assessment.dueDate ?? null,
      // ActivityData expects ActivityState | undefined (not null), so coerce.
      activityState: assessment.activityState ?? undefined,
      scheduledDate: assessment.scheduledDate,
    };
  }, [assessment]);

  const handleSaveAssessmentConfig = async (formData: AssessmentFormActivityData) => {
    if (!assessmentId) {
      setAssessmentSaveError("Assessment context is incomplete.");
      return;
    }

    setAssessmentSaveError("");

    try {
      const response = await updateAssessmentConfigMutation({
        variables: {
          input: {
            assessmentId: assessmentId,
            assessmentType: formData.assessmentType ?? AssessmentType.SocraticDialog,
            numberOfQuestions: formData.numberOfQuestions ?? 10,
            timeLimit: formData.timeLimit ?? 0,
            aiTutorIds: formData.aiTutorIds ?? [],
            freeTextInstructions: formData.freeTextInstructions ?? "",
            activityState: formData.activityState,
            scheduledDate: formData.scheduledDate,
            dueDate: formData.dueDate ?? null,
            ...(moduleId ? { moduleId } : {}),
          },
        },
      });

      const payload = response?.data?.updateAssessmentConfig;
      if (!payload?.success) {
        const message = payload?.message || "Failed to save assessment settings.";
        setAssessmentSaveError(message);
        showToast({
          message,
          type: "error",
        });
        return;
      }

      showToast({
        message: payload.message || "Assessment settings updated.",
        type: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
      const message = "Failed to save assessment settings.";
      setAssessmentSaveError(message);
      showToast({
        message,
        type: "error",
      });
    }
  };

  const handleCancelAssessmentEdit = () => {
    setAssessmentSaveError("");
    setActiveTab(SUBMISSIONS_TAB_KEY);
  };

  if (!canEdit) {
    return <LTIErrorView error="unknown" message="You do not have permission to manage this assessment." />;
  }

  if (!authReady || assessmentLoading) {
    return (
      <LTILayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </LTILayout>
    );
  }

  if (!assessment) {
    return <LTIErrorView error="missing_activity" message="Assessment not found." />;
  }

  return (
    <LTILayout>
      <View style={styles.container}>
        <View style={styles.contentCard}>
          <CustomText text={assessmentName || "Assessment"} size="xl" weight="ultraBold" useTranslationText={false} />
          <CustomText
            text={t("text.lti_assessment_grading_subtitle")}
            size="s"
            useTranslationText={false}
            style={styles.subtitle}
          />

          <View style={styles.tabNavWrapper}>
            <AdminTabNavigator
              tabData={[
                { key: SUBMISSIONS_TAB_KEY, title: t("text.lti_submissions_grading_tab") },
                { key: EDIT_ASSESSMENT_TAB_KEY, title: t("text.lti_edit_assessment_tab") },
              ]}
              activeTab={activeTab}
              onTabPress={setActiveTab}
            />
          </View>

          {activeTab === SUBMISSIONS_TAB_KEY && (
            <View style={styles.tableSection}>
              {submissions.length > 0 && (
                <View style={styles.actionBar}>
                  {selectedSubmissionIds.length > 1 && (
                    <CustomButton
                      text={t("text.lti_approve_post_grades")}
                      useTranslationText={false}
                      size="s"
                      styleType="lightStroke"
                      onPress={() => setIsConfirmModalVisible(true)}
                      disabled={isBulkApproving}
                      leftIcon={null}
                      rightIcon={null}
                    />
                  )}
                  <CustomButton
                    text={t("text.lti_publish_all_grades")}
                    useTranslationText={false}
                    size="s"
                    styleType="primary"
                    onPress={() => setIsPublishConfirmModalVisible(true)}
                    disabled={isBulkApproving}
                    leftIcon={null}
                    rightIcon={null}
                  />
                </View>
              )}
              <GradingCenterTable
                variant="lti-assessment"
                ltiAssessmentRows={submissions}
                ltiAssessmentLoading={gradingLoading}
                selectedSubmissionIds={selectedSubmissionIds}
                onToggleSubmissionSelection={handleToggleSubmissionSelection}
                onToggleSelectAllSubmissions={handleToggleSelectAllSubmissions}
                onLtiEditGradePress={handleLtiEditGradePress}
              />
            </View>
          )}

          {activeTab === EDIT_ASSESSMENT_TAB_KEY && (
            <View style={styles.formSection}>
              {assessmentActivityData ? (
                <AssessmentForm
                  activityData={assessmentActivityData}
                  onSave={handleSaveAssessmentConfig}
                  onCancel={handleCancelAssessmentEdit}
                  error={assessmentSaveError}
                  isSubmitting={isSavingAssessment}
                  isNewActivity={false}
                  activeModuleData={activeModuleData}
                  courseId={courseId}
                  showFreeTextInstructionsInput={true}
                  lmsAiTutors={lmsAiTutors}
                />
              ) : (
                <CustomText
                  text={t("text.lti_assessment_context_incomplete")}
                  size="s"
                  useTranslationText={false}
                  style={styles.errorText}
                />
              )}
            </View>
          )}
        </View>
      </View>

      <CustomModal
        visible={isConfirmModalVisible}
        onRequestClose={() => setIsConfirmModalVisible(false)}
        title={t("text.lti_confirm_grade_posting_title")}
        showCloseButton>
        <View style={styles.confirmationBody}>
          <CustomText
            text={t("text.lti_approve_post_grades_confirm", { count: selectedSubmissionIds.length })}
            size="s"
            useTranslationText={false}
          />
          <View style={styles.confirmationActions}>
            <CustomButton
              text={t("text.publish_grade_cancel")}
              useTranslationText={false}
              styleType="primaryTransparentStroke"
              onPress={() => setIsConfirmModalVisible(false)}
              disabled={isBulkApproving}
              leftIcon={null}
              rightIcon={null}
            />
            <CustomButton
              text={isBulkApproving ? t("text.lti_submitting") : t("text.publish_grade_confirm")}
              useTranslationText={false}
              styleType="primary"
              onPress={handleBulkApproveConfirm}
              disabled={isBulkApproving}
              leftIcon={null}
              rightIcon={null}
            />
          </View>
        </View>
      </CustomModal>

      <CustomModal
        visible={isPublishConfirmModalVisible}
        onRequestClose={() => setIsPublishConfirmModalVisible(false)}
        title={t("text.confirm_grade_publishing_title")}
        showCloseButton>
        <View style={styles.confirmationBody}>
          <CustomText
            text={t("text.lti_publish_all_grades_confirm", {
              count: submissions.length,
              assessmentName: assessmentName || t("text.lti_this_assessment"),
            })}
            size="s"
            useTranslationText={false}
          />
          <View style={styles.confirmationActions}>
            <CustomButton
              text={t("text.publish_grade_cancel")}
              useTranslationText={false}
              styleType="primaryTransparentStroke"
              onPress={() => setIsPublishConfirmModalVisible(false)}
              disabled={isBulkApproving}
              leftIcon={null}
              rightIcon={null}
            />
            <CustomButton
              text={isBulkApproving ? t("text.lti_publishing") : t("text.publish_grade_confirm")}
              useTranslationText={false}
              styleType="primary"
              onPress={handlePublishAllConfirm}
              disabled={isBulkApproving}
              leftIcon={null}
              rightIcon={null}
            />
          </View>
        </View>
      </CustomModal>

      <GradingPanel
        modalState={gradingModalState}
        setModalState={setGradingModalState}
        setActionResult={handleGradingActionResult}
        setUpdateError={handleGradingUpdateError}
        enablePublishGradeConfirmation
        onPublishGrade={handleSingleSubmissionPublish}
      />
    </LTILayout>
  );
}

function LTIAssessmentGradingView() {
  return (
    <GradingCenterSubmissionsProvider>
      <LTIAssessmentGradingViewContent />
    </GradingCenterSubmissionsProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#F8FAFC",
  },
  contentCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    padding: 20,
  },
  subtitle: {
    color: "#64748B",
    marginTop: 4,
    marginBottom: 16,
  },
  tabNavWrapper: {
    marginBottom: 8,
  },
  tableSection: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
  },
  actionBar: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    gap: 12,
  },
  formSection: {
    flex: 1,
  },
  loaderContainer: {
    marginTop: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  confirmationBody: {
    padding: 24,
  },
  confirmationActions: {
    marginTop: 20,
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  errorText: {
    color: "#B91C1C",
  },
});

export default LTIAssessmentGradingView;
